import { 
  collection, 
  query, 
  where, 
  getDocs, 
  setDoc, 
  updateDoc,
  doc, 
  Timestamp,
  limit,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AdminLog, UserAnalytics, AdminDashboardStats, SystemNotification } from '../types';
import { LoyaltyProgram, Transaction, User } from '../types';

/**
 * Admin Service - Handles all admin panel operations
 */
class AdminService {
  static readonly ADMIN_LOGS_COLLECTION = 'adminLogs';
  static readonly SYSTEM_NOTIFICATIONS_COLLECTION = 'systemNotifications';
  static readonly USERS_COLLECTION = 'users';

  /**
   * Get all users with admin stats
   */
  async getAllUsers(): Promise<(User & { programCount?: number; transactionCount?: number })[]> {
    try {
      const usersRef = collection(db, AdminService.USERS_COLLECTION);
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const users: (User & { programCount?: number; transactionCount?: number })[] = [];
      for (const doc of snapshot.docs) {
        const data = doc.data();
        users.push({
          id: doc.id,
          email: data.email,
          displayName: data.displayName,
          photoURL: data.photoURL,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate?.() || new Date(),
          role: data.role || 'user',
          permissions: data.permissions,
        });
      }

      return users;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Get user analytics
   */
  async getUserAnalytics(): Promise<UserAnalytics> {
    try {
      const usersRef = collection(db, AdminService.USERS_COLLECTION);
      const allUsersSnap = await getDocs(usersRef);
      const totalUsers = allUsersSnap.size;

      // Get today's and this week's new users
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

      let newUsersToday = 0;
      let newUsersThisWeek = 0;
      let newUsersThisMonth = 0;
      let activeUsers = 0;

      allUsersSnap.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate?.() || new Date();
        const lastLoginAt = data.lastLoginAt?.toDate?.() || new Date();

        if (createdAt >= today) newUsersToday++;
        if (createdAt >= weekAgo) newUsersThisWeek++;
        if (createdAt >= monthAgo) newUsersThisMonth++;

        // Active if logged in within last 7 days
        if (lastLoginAt >= weekAgo) activeUsers++;
      });

      // Get transaction stats
      const transactionsRef = collection(db, 'userTransactions');
      const transSnap = await getDocs(transactionsRef);
      const totalTransactions = transSnap.size;

      let totalPointsTracked = 0;
      transSnap.forEach(doc => {
        const data = doc.data();
        totalPointsTracked += data.points || 0;
      });

      return {
        totalUsers,
        activeUsers,
        newUsersToday,
        newUsersThisWeek,
        newUsersThisMonth,
        totalTransactions,
        totalPointsTracked,
        lastUpdated: new Date(),
      };
    } catch (error) {
      console.error('Error calculating user analytics:', error);
      throw error;
    }
  }

  /**
   * Get program popularity stats
   */
  async getProgramStats(): Promise<{ totalPrograms: number; mostPopular: Array<{ name: string; usageCount: number }> }> {
    try {
      const programsRef = collection(db, 'userLoyaltyPrograms');
      const snapshot = await getDocs(programsRef);

      const programStats = new Map<string, number>();
      snapshot.forEach(doc => {
        const data = doc.data();
        const name = data.name || 'Unknown';
        programStats.set(name, (programStats.get(name) || 0) + 1);
      });

      const mostPopular = Array.from(programStats.entries())
        .map(([name, count]) => ({ name, usageCount: count }))
        .sort((a, b) => b.usageCount - a.usageCount)
        .slice(0, 10);

      return {
        totalPrograms: snapshot.size,
        mostPopular,
      };
    } catch (error) {
      console.error('Error fetching program stats:', error);
      throw error;
    }
  }

  /**
   * Get transaction stats
   */
  async getTransactionStats(): Promise<{ totalCount: number; todayCount: number; totalPointsVolume: number }> {
    try {
      const transRef = collection(db, 'userTransactions');
      const snapshot = await getDocs(transRef);

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      let todayCount = 0;
      let totalPointsVolume = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        const txnDate = data.date?.toDate?.() || new Date();

        totalPointsVolume += Math.abs(data.points || 0);
        if (txnDate >= today) todayCount++;
      });

      return {
        totalCount: snapshot.size,
        todayCount,
        totalPointsVolume,
      };
    } catch (error) {
      console.error('Error fetching transaction stats:', error);
      throw error;
    }
  }

  /**
   * Get admin dashboard stats
   */
  async getDashboardStats(): Promise<AdminDashboardStats> {
    try {
      const [users, programs, transactions] = await Promise.all([
        this.getUserAnalytics(),
        this.getProgramStats(),
        this.getTransactionStats(),
      ]);

      return {
        users,
        programs,
        transactions,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Create system notification
   */
  async createSystemNotification(
    createdBy: string,
    title: string,
    message: string,
    type: 'announcement' | 'alert' | 'update' = 'announcement',
    targetUsers: 'all' | string[] = 'all',
    priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium',
    expiresAt?: Date
  ): Promise<string> {
    try {
      const notifRef = doc(collection(db, AdminService.SYSTEM_NOTIFICATIONS_COLLECTION));
      const notification: SystemNotification = {
        id: notifRef.id,
        createdBy,
        title,
        message,
        type,
        targetUsers,
        priority,
        createdAt: new Date(),
        expiresAt,
        read: false,
      };

      await setDoc(notifRef, {
        ...notification,
        createdAt: Timestamp.fromDate(notification.createdAt),
        expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : null,
      });

      console.log('✅ System notification created:', notifRef.id);
      return notifRef.id;
    } catch (error) {
      console.error('Error creating system notification:', error);
      throw error;
    }
  }

  /**
   * Get system notifications
   */
  async getSystemNotifications(limit_count: number = 50): Promise<SystemNotification[]> {
    try {
      const notifsRef = collection(db, AdminService.SYSTEM_NOTIFICATIONS_COLLECTION);
      const q = query(
        notifsRef,
        orderBy('createdAt', 'desc'),
        limit(limit_count)
      );

      const snapshot = await getDocs(q);
      const notifications: SystemNotification[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          createdBy: data.createdBy,
          title: data.title,
          message: data.message,
          type: data.type,
          targetUsers: data.targetUsers,
          priority: data.priority,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          expiresAt: data.expiresAt?.toDate?.() || undefined,
          read: data.read || false,
        });
      });

      return notifications;
    } catch (error) {
      console.error('Error fetching system notifications:', error);
      throw error;
    }
  }

  /**
   * Get admin audit logs
   */
  async getAuditLogs(limit_count: number = 100): Promise<AdminLog[]> {
    try {
      const logsRef = collection(db, AdminService.ADMIN_LOGS_COLLECTION);
      const q = query(
        logsRef,
        orderBy('timestamp', 'desc'),
        limit(limit_count)
      );

      const snapshot = await getDocs(q);
      const logs: AdminLog[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          adminId: data.adminId,
          adminEmail: data.adminEmail,
          action: data.action,
          targetId: data.targetId,
          targetType: data.targetType,
          changes: data.changes,
          timestamp: data.timestamp?.toDate?.() || new Date(),
          details: data.details,
        });
      });

      return logs;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  /**
   * Delete a user (admin only)
   */
  async deleteUser(adminId: string, userId: string): Promise<void> {
    try {
      // Note: This just removes Firestore data. Auth user deletion should be done separately
      const userRef = doc(db, AdminService.USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        deletedAt: Timestamp.now(),
        deletedBy: adminId,
      });

      console.log('✅ User marked for deletion:', userId);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Ban/Unban a user
   */
  async toggleUserBan(adminId: string, userId: string, banned: boolean): Promise<void> {
    try {
      const userRef = doc(db, AdminService.USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        banned,
        bannedAt: banned ? Timestamp.now() : null,
        bannedBy: banned ? adminId : null,
      });

      console.log(`✅ User ${banned ? 'banned' : 'unbanned'}:`, userId);
    } catch (error) {
      console.error('Error toggling user ban:', error);
      throw error;
    }
  }

  /**
   * Get user details with all their data
   */
  async getUserDetails(userId: string): Promise<any> {
    try {
      const userRef = doc(db, AdminService.USERS_COLLECTION, userId);
      const userSnap = await getDocs(query(collection(db, AdminService.USERS_COLLECTION), where('__name__', '==', userId)));

      if (userSnap.empty) {
        throw new Error('User not found');
      }

      const userData = userSnap.docs[0].data();

      // Get user programs
      const programsRef = collection(db, 'userLoyaltyPrograms');
      const programsQ = query(programsRef, where('userId', '==', userId));
      const programsSnap = await getDocs(programsQ);

      // Get user transactions
      const transRef = collection(db, 'userTransactions');
      const transQ = query(transRef, where('userId', '==', userId));
      const transSnap = await getDocs(transQ);

      return {
        ...userData,
        programsCount: programsSnap.size,
        transactionsCount: transSnap.size,
        programs: programsSnap.docs.map(d => d.data()),
        recentTransactions: transSnap.docs.slice(0, 10).map(d => d.data()),
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }
}

export const adminService = new AdminService();
export default adminService;
